import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, FileText, Download, RefreshCw, CheckCircle, 
  FileEdit, Save, Copy, Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Invoice field labels for display
const FIELD_LABELS = {
  invoice_number: "Invoice Number",
  invoice_date: "Invoice Date",
  due_date: "Due Date",
  vendor_name: "Vendor Name",
  vendor_address: "Vendor Address",
  vendor_phone: "Vendor Phone",
  vendor_email: "Vendor Email",
  customer_name: "Customer Name",
  customer_address: "Customer Address",
  customer_phone: "Customer Phone",
  customer_email: "Customer Email",
  subtotal: "Subtotal",
  tax_rate: "Tax Rate",
  tax_amount: "Tax Amount",
  discount: "Discount",
  total_amount: "Total Amount",
  currency: "Currency",
  payment_terms: "Payment Terms",
  po_number: "PO Number",
  notes: "Notes"
};

export default function ProcessDocument() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  
  // Rename configuration
  const [selectedFields, setSelectedFields] = useState([]);
  const [separator, setSeparator] = useState("_");
  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  const [previewFilename, setPreviewFilename] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchDocument();
  }, [documentId]);

  useEffect(() => {
    // Update preview filename whenever rename config changes
    if (document?.extracted_data) {
      generatePreviewFilename();
    }
  }, [selectedFields, separator, prefix, suffix, document]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}`, {
        headers: getAuthHeaders()
      });
      setDocument(response.data);
      
      // Pre-select some common fields if processed
      if (response.data.status === "processed" && response.data.extracted_data) {
        const data = response.data.extracted_data;
        const defaultFields = ["invoice_number", "vendor_name", "invoice_date"]
          .filter(f => data[f]);
        setSelectedFields(defaultFields);
      }
    } catch (error) {
      toast.error("Failed to load document");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/documents/${documentId}/process`,
        {},
        { headers: getAuthHeaders() }
      );
      setDocument(prev => ({
        ...prev,
        status: "processed",
        extracted_data: response.data.extracted_data
      }));
      toast.success("Document processed successfully!");
      
      // Pre-select common fields
      const data = response.data.extracted_data;
      const defaultFields = ["invoice_number", "vendor_name", "invoice_date"]
        .filter(f => data[f]);
      setSelectedFields(defaultFields);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const generatePreviewFilename = () => {
    if (!document?.extracted_data) return;
    
    const parts = [];
    if (prefix) parts.push(prefix);
    
    for (const field of selectedFields) {
      const value = document.extracted_data[field];
      if (value) {
        let cleanValue = String(value).replace(/[/\\:]/g, "-");
        cleanValue = cleanValue.replace(/[^a-zA-Z0-9\-_. ]/g, "").trim();
        parts.push(cleanValue);
      }
    }
    
    if (suffix) parts.push(suffix);
    
    const ext = document.original_filename.split(".").pop();
    const filename = parts.length > 0 ? parts.join(separator) + "." + ext : document.original_filename;
    setPreviewFilename(filename);
  };

  const handleRename = async () => {
    if (selectedFields.length === 0) {
      toast.error("Please select at least one field for renaming");
      return;
    }
    
    setRenaming(true);
    try {
      const response = await axios.post(
        `${API}/documents/${documentId}/rename`,
        {
          document_id: documentId,
          selected_fields: selectedFields,
          separator,
          prefix: prefix || null,
          suffix: suffix || null
        },
        { headers: getAuthHeaders() }
      );
      
      setDocument(prev => ({
        ...prev,
        renamed_filename: response.data.renamed_filename
      }));
      
      toast.success("Document renamed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Renaming failed");
    } finally {
      setRenaming(false);
    }
  };

  const handleDownload = async () => {
    try {
      const filename = document.renamed_filename || document.original_filename;
      
      toast.info(`Preparing download: ${filename}`);
      
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        headers: getAuthHeaders(),
        responseType: "blob",
        withCredentials: true
      });
      
      // Create blob with correct content type
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      
      // Use different methods based on browser support
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // For IE/Edge legacy
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        // For modern browsers
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create and configure download link
        const downloadLink = window.document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = filename;
        downloadLink.style.cssText = "display:none;visibility:hidden;";
        
        // Append to body
        window.document.body.appendChild(downloadLink);
        
        // Use setTimeout to ensure the link is in DOM before clicking
        setTimeout(() => {
          downloadLink.click();
          
          // Cleanup after download starts
          setTimeout(() => {
            if (downloadLink.parentNode) {
              window.document.body.removeChild(downloadLink);
            }
            window.URL.revokeObjectURL(blobUrl);
          }, 2000);
        }, 100);
      }
      
      toast.success(`Download started: ${filename}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download document. Please try again.");
    }
  };

  const toggleField = (field) => {
    setSelectedFields(prev => 
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4f46e5]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="app-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-[#4f46e5]" />
              <h1 className="font-heading text-lg font-semibold" data-testid="document-title">
                {document?.original_filename}
              </h1>
            </div>
          </div>
          <Button 
            onClick={handleDownload}
            className="bg-[#4f46e5] hover:bg-[#4338ca]"
            data-testid="download-doc-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </header>

      {/* Main Content - Split View */}
      <main className="split-view">
        {/* Left Panel - Document Preview / Process Button */}
        <div className="border-r bg-muted/30 p-6 overflow-auto">
          {document?.status === "pending" ? (
            <Card className="max-w-md mx-auto mt-12">
              <CardHeader className="text-center">
                <Sparkles className="h-12 w-12 text-[#4f46e5] mx-auto mb-4" />
                <CardTitle className="font-heading">Ready to Process</CardTitle>
                <CardDescription>
                  Click the button below to extract data from this invoice using AI-powered OCR
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={handleProcess}
                  disabled={processing}
                  className="w-full bg-[#4f46e5] hover:bg-[#4338ca]"
                  size="lg"
                  data-testid="process-ocr-btn"
                >
                  {processing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Extract Invoice Data
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Extracted Data
              </h2>
              <Card data-testid="extracted-data-card">
                <CardContent className="p-6 space-y-4">
                  {document?.extracted_data && Object.entries(document.extracted_data).map(([key, value]) => {
                    // Skip line_items for now (complex array)
                    if (key === "line_items") return null;
                    if (!value) return null;
                    
                    return (
                      <div key={key} className="flex justify-between items-start gap-4">
                        <span className="text-sm text-muted-foreground min-w-[120px]">
                          {FIELD_LABELS[key] || key}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="data-text text-right" data-testid={`field-${key}`}>
                            {String(value)}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(String(value))}
                            className="h-6 w-6 p-0"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Line Items */}
                  {document?.extracted_data?.line_items && document.extracted_data.line_items.length > 0 && (
                    <div className="pt-4">
                      <Separator className="mb-4" />
                      <h3 className="text-sm font-medium mb-3">Line Items</h3>
                      <div className="space-y-2">
                        {document.extracted_data.line_items.map((item, idx) => (
                          <div key={idx} className="bg-muted/50 p-3 rounded-md text-sm">
                            <div className="flex justify-between">
                              <span className="truncate">{item.description}</span>
                              <span className="data-text font-medium ml-4">
                                {item.amount || (item.quantity && item.unit_price ? `${item.quantity} × ${item.unit_price}` : "")}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Right Panel - Rename Configuration */}
        <div className="p-6 overflow-auto">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <FileEdit className="h-5 w-5 text-[#4f46e5]" />
            Rename Document
          </h2>
          
          {document?.status !== "processed" ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                <p>Process the document first to enable renaming</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Current / New Filename */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Filename Preview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Original</Label>
                    <p className="data-text text-sm truncate" data-testid="original-filename">
                      {document.original_filename}
                    </p>
                  </div>
                  {document.renamed_filename && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Current Renamed</Label>
                      <p className="data-text text-sm truncate text-green-600" data-testid="current-renamed">
                        {document.renamed_filename}
                      </p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-muted-foreground">Preview</Label>
                    <div className="bg-[#e0e7ff] border-l-4 border-[#4f46e5] p-3 rounded-r-md">
                      <p className="data-text text-sm truncate" data-testid="preview-filename">
                        {previewFilename || "Select fields below..."}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Field Selection */}
              <Card data-testid="field-selection-card">
                <CardHeader>
                  <CardTitle className="text-base">Select Fields for Filename</CardTitle>
                  <CardDescription>
                    Choose which extracted fields to include in the new filename
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {document.extracted_data && Object.entries(document.extracted_data).map(([key, value]) => {
                      if (key === "line_items" || !value) return null;
                      
                      const isSelected = selectedFields.includes(key);
                      
                      return (
                        <div 
                          key={key}
                          className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-all ${
                            isSelected ? "bg-[#4f46e5] text-white border-[#4f46e5]" : "hover:bg-muted"
                          }`}
                          onClick={() => toggleField(key)}
                          data-testid={`field-checkbox-${key}`}
                        >
                          <Checkbox 
                            checked={isSelected}
                            className={isSelected ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-[#4f46e5]" : ""}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {FIELD_LABELS[key] || key}
                            </p>
                            <p className={`text-xs truncate ${isSelected ? "text-white/70" : "text-muted-foreground"}`}>
                              {String(value).substring(0, 30)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Rename Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Rename Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="prefix">Prefix</Label>
                      <Input
                        id="prefix"
                        value={prefix}
                        onChange={(e) => setPrefix(e.target.value)}
                        placeholder="e.g., INV"
                        data-testid="prefix-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="separator">Separator</Label>
                      <Input
                        id="separator"
                        value={separator}
                        onChange={(e) => setSeparator(e.target.value)}
                        placeholder="_"
                        data-testid="separator-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="suffix">Suffix</Label>
                      <Input
                        id="suffix"
                        value={suffix}
                        onChange={(e) => setSuffix(e.target.value)}
                        placeholder="e.g., final"
                        data-testid="suffix-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Rename Button */}
              <Button 
                onClick={handleRename}
                disabled={renaming || selectedFields.length === 0}
                className="w-full bg-[#4f46e5] hover:bg-[#4338ca]"
                size="lg"
                data-testid="rename-btn"
              >
                {renaming ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Renaming...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save New Filename
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
